import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Card, Button, Alert, Typography, Space, Spin, Result, Descriptions, Form, Input } from 'antd';
import { LockOutlined, CheckCircleOutlined, SafetyOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { validatePasswordResetToken, resetPassword } from '../lib/api';

const { Title, Text } = Typography;

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [isValidating, setIsValidating] = useState(true);
  const [isValid, setIsValid] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState<'info' | 'form' | 'success'>('info');
  const [userId, setUserId] = useState<number | null>(null);
  const [userInfo, setUserInfo] = useState<{ email?: string; username?: string; name?: string } | null>(null);
  const [form] = Form.useForm();

  useEffect(() => {
    if (!token) {
      setIsValidating(false);
      setValidationError('缺少重置連結憑證');
      return;
    }

    validatePasswordResetToken(token)
      .then((data) => {
        setIsValidating(false);
        if (data.valid) {
          setIsValid(true);
          setUserId(data.userId ?? null);
          setUserInfo(data.user ?? null);
        } else {
          setValidationError(data.error || '無效的重置連結');
        }
      })
      .catch(() => {
        setIsValidating(false);
        setValidationError('驗證連結時發生錯誤');
      });
  }, [token]);

  const handleStartReset = () => {
    setStep('form');
  };

  const handleResetPassword = async (values: { newPassword: string; confirmPassword: string }) => {
    if (!userId || !token) return;

    if (values.newPassword !== values.confirmPassword) {
      setError('兩次輸入的密碼不一致');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await resetPassword({
        token,
        userId,
        newPassword: values.newPassword,
      });

      setStep('success');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (isValidating) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5' }}>
        <Card>
          <div style={{ textAlign: 'center' }}>
            <Spin size="large" />
            <Text style={{ display: 'block', marginTop: 16 }}>驗證中...</Text>
          </div>
        </Card>
      </div>
    );
  }

  if (!isValid) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5', padding: 24 }}>
        <Card style={{ width: '100%', maxWidth: 420 }}>
          <Alert
            title="無效的連結"
            description={
              <Space orientation="vertical">
                <Text>{validationError}</Text>
                <Text>此重置連結可能已過期或已被使用。</Text>
                <Text>請聯繫管理員取得新的重置連結。</Text>
                <Link to="/login">
                  <Button type="primary">返回登入頁面</Button>
                </Link>
              </Space>
            }
            type="error"
            showIcon
          />
        </Card>
      </div>
    );
  }

  if (step === 'success') {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5', padding: 24 }}>
        <Card style={{ width: '100%', maxWidth: 420 }}>
          <Result
            status="success"
            icon={<CheckCircleOutlined />}
            title="密碼重置完成！"
            subTitle="您的密碼已成功重置，請使用新密碼登入。"
            extra={[
              <Link to="/login" key="login">
                <Button type="primary">前往登入</Button>
              </Link>,
            ]}
          />
        </Card>
      </div>
    );
  }

  if (step === 'form') {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', padding: 24 }}>
        <Card style={{ width: '100%', maxWidth: 420, boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}>
          <Space orientation="vertical" style={{ width: '100%' }} size="large">
            <div style={{ textAlign: 'center' }}>
              <SafetyOutlined style={{ fontSize: 64, color: '#1890ff', marginBottom: 16 }} />
              <Title level={3} style={{ margin: 0 }}>設定新密碼</Title>
              <Text type="secondary">請設定您的新登入密碼</Text>
            </div>

            {error && <Alert title={error} type="error" showIcon closable />}

            <Alert
              title="密碼要求"
              description={
                <ul style={{ paddingLeft: 20, margin: '4px 0' }}>
                  <li>至少 8 個字符</li>
                  <li>包含大寫字母 (A-Z)</li>
                  <li>包含小寫字母 (a-z)</li>
                  <li>包含數字 (0-9)</li>
                </ul>
              }
              type="info"
              showIcon
            />

            <Form
              form={form}
              layout="vertical"
              onFinish={handleResetPassword}
            >
              <Form.Item
                name="newPassword"
                label="新密碼"
                rules={[
                  { required: true, message: '請輸入新密碼' },
                  { min: 8, message: '密碼至少 8 個字符' },
                  { pattern: /[A-Z]/, message: '密碼必須包含至少一個大寫字母' },
                  { pattern: /[a-z]/, message: '密碼必須包含至少一個小寫字母' },
                  { pattern: /[0-9]/, message: '密碼必須包含至少一個數字' },
                ]}
              >
                <Input.Password placeholder="輸入新密碼" />
              </Form.Item>

              <Form.Item
                name="confirmPassword"
                label="確認密碼"
                rules={[
                  { required: true, message: '請再次輸入新密碼' },
                ]}
              >
                <Input.Password placeholder="再次輸入新密碼" />
              </Form.Item>

              <Form.Item>
                <Button
                  type="primary"
                  size="large"
                  block
                  htmlType="submit"
                  loading={loading}
                >
                  重置密碼
                </Button>
              </Form.Item>
            </Form>
          </Space>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', padding: 24 }}>
      <Card style={{ width: '100%', maxWidth: 420, boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}>
        <Space orientation="vertical" style={{ width: '100%' }} size="large">
          <div style={{ textAlign: 'center' }}>
            <LockOutlined style={{ fontSize: 64, color: '#1890ff', marginBottom: 16 }} />
            <Title level={3} style={{ margin: 0 }}>重置密碼</Title>
            <Text type="secondary">帳號救援流程</Text>
          </div>

          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="目標帳號">
              {userInfo?.name || userInfo?.username || userInfo?.email || 'Unknown User'}
            </Descriptions.Item>
            {userInfo?.email && <Descriptions.Item label="Email">{userInfo.email}</Descriptions.Item>}
            {userInfo?.username && <Descriptions.Item label="帳號">{userInfo.username}</Descriptions.Item>}
          </Descriptions>

          <Alert
            title="重要提醒"
            description={
              <ul style={{ paddingLeft: 20, margin: '8px 0' }}>
                <li>此連結為一次性使用，重置後將自動失效</li>
                <li>新密碼設定完成後，舊密碼將無法使用</li>
                <li>請確保您正在為正確的帳號設定新密碼</li>
              </ul>
            }
            type="warning"
            showIcon
            icon={<ExclamationCircleOutlined />}
          />

          <Button
            type="primary"
            size="large"
            block
            onClick={handleStartReset}
          >
            開始重置密碼
          </Button>

          <div style={{ textAlign: 'center' }}>
            <Text type="secondary">不是您要的帳號？ <Link to="/login">返回登入</Link></Text>
          </div>
        </Space>
      </Card>
    </div>
  );
}
